import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Package, Warning, ShieldCheck, HardHat } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ========== DEMAND DASHBOARD (Phase D) ==========
export const DemandDashboard = ({ projectId }) => {
  const [dashboard, setDashboard] = useState(null);
  const [overrides, setOverrides] = useState(null);
  const [workforceTarget, setWorkforceTarget] = useState('');
  const [workforceResult, setWorkforceResult] = useState(null);

  const fetchDashboard = useCallback(async () => {
    try {
      const [dashRes, ovRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/demand-dashboard`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/override-summary`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setDashboard(dashRes.data);
      setOverrides(ovRes.data);
    } catch { /* ignore */ }
  }, [projectId]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const calculateWorkforce = async () => {
    if (!workforceTarget || parseInt(workforceTarget) <= 0) return;
    try {
      const res = await axios.post(`${BACKEND_URL}/api/projects/${projectId}/manpower-demand/calculate-workforce`,
        { target_duration_days: parseInt(workforceTarget) }, { withCredentials: true });
      setWorkforceResult(res.data.calculations);
    } catch { toast.error('Failed to calculate'); }
  };

  const rc = dashboard?.recipe_coverage || {};
  const mp = dashboard?.material_pipeline || {};
  const ms = dashboard?.manpower_summary || {};

  return (
    <div className="space-y-8" data-testid="demand-dashboard">
      {dashboard && (
        <div className="bg-white border border-zinc-200 p-5" data-testid="demand-summary-strip">
          <h2 className="text-base font-bold text-zinc-900 mb-4">Demand Overview</h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="border border-zinc-200 p-3">
              <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider mb-2">Recipe Coverage</p>
              <div className="flex items-end gap-3">
                <span className={`text-2xl font-bold ${rc.coverage_percentage >= 80 ? 'text-emerald-700' : rc.coverage_percentage >= 50 ? 'text-amber-700' : 'text-red-700'}`} data-testid="dash-recipe-pct">
                  {rc.coverage_percentage || 0}%
                </span>
                <span className="text-xs text-zinc-500 mb-1">{rc.with_material_recipe || 0} mat / {rc.with_manpower_recipe || 0} mp / {rc.total_items || 0} items</span>
              </div>
              {rc.flagged_items?.length > 0 && (
                <p className="text-[10px] text-amber-600 mt-1.5 font-medium">{rc.flagged_items.length} item{rc.flagged_items.length !== 1 ? 's' : ''} flagged</p>
              )}
            </div>
            <div className="border border-zinc-200 p-3">
              <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider mb-2">Material Pipeline</p>
              {mp.has_snapshot ? (
                <div className="flex items-end gap-3">
                  <div className="flex gap-2">
                    <span className="text-emerald-700 font-bold text-lg">{mp.fully_covered}</span>
                    <span className="text-amber-700 font-bold text-lg">{mp.partially_covered}</span>
                    <span className="text-red-700 font-bold text-lg" data-testid="dash-mat-uncovered">{mp.uncovered}</span>
                  </div>
                  <span className="text-[9px] text-zinc-400 mb-1">covered / partial / uncovered</span>
                </div>
              ) : (
                <p className="text-xs text-zinc-400 italic">Not yet exploded</p>
              )}
              {mp.updated_by && <p className="text-[9px] text-zinc-400 mt-1.5" data-testid="mat-updated-by">Updated by {mp.updated_by} {mp.snapshot_date ? `on ${new Date(mp.snapshot_date).toLocaleDateString()}` : ''}</p>}
            </div>
            <div className="border border-zinc-200 p-3">
              <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider mb-2">Manpower</p>
              {ms.has_snapshot ? (
                <div>
                  <div className="flex items-end gap-3">
                    <span className="text-blue-700 font-bold text-lg" data-testid="dash-mp-days">{ms.total_man_days}</span>
                    <span className="text-xs text-zinc-500 mb-0.5">man-days required</span>
                  </div>
                  <div className="flex gap-2 mt-1">
                    {Object.entries(ms.by_labour_type || {}).map(([t, d]) => (
                      <span key={t} className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${t === 'Skilled' ? 'bg-blue-50 text-blue-700' : t === 'Unskilled' ? 'bg-zinc-100 text-zinc-600' : 'bg-amber-50 text-amber-700'}`}>{t}: {d}d</span>
                    ))}
                  </div>
                  {ms.planning_gap !== 0 && (
                    <p className={`text-[10px] mt-1 font-medium ${ms.planning_gap > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {ms.planning_gap > 0 ? `${ms.planning_gap}d under-planned` : `${Math.abs(ms.planning_gap)}d over-planned`}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-xs text-zinc-400 italic">Not yet derived</p>
              )}
              {ms.updated_by && <p className="text-[9px] text-zinc-400 mt-1.5" data-testid="mp-updated-by">Updated by {ms.updated_by} {ms.snapshot_date ? `on ${new Date(ms.snapshot_date).toLocaleDateString()}` : ''}</p>}
            </div>
          </div>
        </div>
      )}
      <MaterialDemandView projectId={projectId} onRefresh={fetchDashboard} />
      <ManpowerDemandView projectId={projectId} onRefresh={fetchDashboard} />

      {/* Override Summary */}
      {overrides && overrides.total_items > 0 && (
        <div className="bg-white border border-zinc-200 p-4" data-testid="override-summary">
          <h3 className="text-sm font-bold text-zinc-900 mb-3">Override Tracking</h3>
          <div className="grid grid-cols-4 gap-3 mb-3">
            <div className="border border-zinc-200 p-2.5"><p className="text-[9px] text-zinc-500 font-bold uppercase">Custom Items</p><p className="text-lg font-bold text-zinc-800">{overrides.custom_item_count} <span className="text-[10px] text-zinc-400">({overrides.custom_item_pct}%)</span></p></div>
            <div className="border border-zinc-200 p-2.5"><p className="text-[9px] text-zinc-500 font-bold uppercase">Recipe Override</p><p className="text-lg font-bold text-zinc-800">{overrides.recipe_override_count} <span className="text-[10px] text-zinc-400">({overrides.recipe_override_pct}%)</span></p></div>
            <div className="border border-zinc-200 p-2.5"><p className="text-[9px] text-zinc-500 font-bold uppercase">Rate Override</p><p className="text-lg font-bold text-zinc-800">{overrides.rate_override_count} <span className="text-[10px] text-zinc-400">({overrides.rate_override_pct}%)</span></p></div>
            <div className="border border-zinc-200 p-2.5"><p className="text-[9px] text-zinc-500 font-bold uppercase">Total Override</p><p className={`text-lg font-bold ${overrides.total_override_pct > 30 ? 'text-red-700' : overrides.total_override_pct > 10 ? 'text-amber-700' : 'text-emerald-700'}`}>{overrides.total_override_pct}%</p></div>
          </div>
          {overrides.total_rate_impact !== 0 && <p className="text-[10px] text-zinc-500">Rate impact: <strong className={overrides.total_rate_impact > 0 ? 'text-red-600' : 'text-emerald-600'}>{overrides.total_rate_impact > 0 ? '+' : ''}{overrides.total_rate_impact.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</strong></p>}
        </div>
      )}

      {/* Workforce Calculator */}
      {dashboard?.manpower_summary?.has_snapshot && (
        <div className="bg-white border border-zinc-200 p-4" data-testid="workforce-calculator">
          <h3 className="text-sm font-bold text-zinc-900 mb-3">Workforce Planner</h3>
          <div className="flex items-end gap-3 mb-3">
            <div><label className="text-[10px] text-zinc-500 font-bold uppercase block mb-1">Target Duration (days)</label><input type="number" value={workforceTarget} onChange={e => setWorkforceTarget(e.target.value)} placeholder="e.g. 30" className="border border-zinc-200 rounded px-3 py-2 text-sm w-32" data-testid="workforce-target-input" /></div>
            <button onClick={calculateWorkforce} className="bg-blue-700 text-white text-xs font-semibold px-4 py-2.5 rounded hover:bg-blue-800" data-testid="workforce-calc-btn">Calculate</button>
          </div>
          {workforceResult && (
            <div className="space-y-1.5">
              {workforceResult.map((c, i) => (
                <div key={i} className="flex items-center justify-between bg-zinc-50 border border-zinc-100 px-3 py-2 rounded text-xs" data-testid={`workforce-result-${i}`}>
                  <div><span className="font-medium text-zinc-800">{c.role_name || 'General'}</span><span className={`ml-2 text-[9px] font-bold px-1.5 py-0.5 rounded ${c.labour_type === 'Skilled' ? 'bg-blue-100 text-blue-700' : c.labour_type === 'Unskilled' ? 'bg-zinc-200 text-zinc-600' : 'bg-amber-100 text-amber-700'}`}>{c.labour_type}</span></div>
                  <span className="font-bold text-zinc-900">{c.decision}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ========== MATERIAL DEMAND VIEW ==========
const MaterialDemandView = ({ projectId, onRefresh }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exploding, setExploding] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedGaps, setSelectedGaps] = useState(new Set());

  const fetchLatest = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/projects/${projectId}/material-demand/latest`, { withCredentials: true });
      setData(res.data);
    } catch { /* no snapshot yet */ }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetchLatest(); }, [fetchLatest]);

  const explodeDemand = async () => {
    setExploding(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/projects/${projectId}/material-demand/explode`, {}, { withCredentials: true });
      setData(res.data);
      toast.success(`Demand exploded: ${res.data.summary?.total_materials || 0} materials`);
      if (onRefresh) onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to explode demand'); }
    setExploding(false);
  };

  const generateIndents = async () => {
    const indices = selectedGaps.size > 0 ? Array.from(selectedGaps) : undefined;
    setGenerating(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/projects/${projectId}/material-demand/generate-indents`,
        { material_indices: indices }, { withCredentials: true });
      toast.success(res.data.message);
      setSelectedGaps(new Set());
      await explodeDemand();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to generate indents'); }
    setGenerating(false);
  };

  const toggleGap = (idx) => { const next = new Set(selectedGaps); if (next.has(idx)) next.delete(idx); else next.add(idx); setSelectedGaps(next); };

  const demands = data?.demands || [];
  const summary = data?.summary || {};
  const warnings = data?.warnings || [];
  const skipped = data?.skipped_items || [];
  const gapIndices = demands.map((d, i) => d.gap_qty > 0 ? i : -1).filter(i => i >= 0);

  if (loading) return <div className="py-8 text-center text-zinc-400 text-sm">Loading demand data...</div>;

  return (
    <div className="space-y-6" data-testid="material-demand-view">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-zinc-900">Material Demand</h2>
          <p className="text-xs text-zinc-500 mt-0.5">Explode BOQ recipes into material demand. Compare against procurement pipeline.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={explodeDemand} disabled={exploding} size="sm" className="bg-blue-700 hover:bg-blue-800 text-white rounded-none text-xs" data-testid="explode-demand-btn">
            {exploding ? 'Exploding...' : data?.demands?.length ? 'Refresh Demand' : 'Explode Demand'}
          </Button>
          {gapIndices.length > 0 && (
            <Button onClick={generateIndents} disabled={generating} size="sm" variant="outline" className="rounded-none border-zinc-300 text-xs" data-testid="generate-indents-btn">
              {generating ? 'Generating...' : `Generate Draft Indents${selectedGaps.size > 0 ? ` (${selectedGaps.size})` : ` (${gapIndices.length} gaps)`}`}
            </Button>
          )}
        </div>
      </div>
      {demands.length === 0 && !data?.snapshot_date && (
        <div className="text-center py-12 bg-white border border-zinc-200">
          <Package size={40} className="mx-auto text-zinc-300 mb-3" />
          <p className="text-zinc-500 text-sm">No demand data yet. Click "Explode Demand" to analyze BOQ recipes.</p>
        </div>
      )}
      {demands.length > 0 && (
        <>
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-white border border-zinc-200 p-3"><p className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider">Total Materials</p><p className="text-xl font-bold text-zinc-900 mt-1" data-testid="demand-total">{summary.total_materials}</p></div>
            <div className="bg-white border border-zinc-200 p-3"><p className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider">Fully Covered</p><p className="text-xl font-bold text-emerald-700 mt-1" data-testid="demand-covered">{summary.fully_covered}</p></div>
            <div className="bg-white border border-zinc-200 p-3"><p className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider">Partially Covered</p><p className="text-xl font-bold text-amber-700 mt-1">{summary.partially_covered}</p></div>
            <div className="bg-white border border-zinc-200 p-3"><p className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider">Uncovered</p><p className="text-xl font-bold text-red-700 mt-1" data-testid="demand-uncovered">{summary.uncovered}</p></div>
          </div>
          {warnings.length > 0 && (
            <div className="bg-amber-50 border border-amber-300 p-3 space-y-1">
              {warnings.map((w, i) => (<div key={i} className="flex items-center gap-2 text-xs text-amber-800"><Warning size={14} className="text-amber-600 shrink-0" /><span>{w.message}</span></div>))}
            </div>
          )}
          <div className="bg-white border border-zinc-200 overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="bg-zinc-50 border-b border-zinc-200 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                <th className="p-2.5 text-left w-8"></th><th className="p-2.5 text-left">Material</th><th className="p-2.5 text-left w-16">Unit</th>
                <th className="p-2.5 text-right w-20">Required</th><th className="p-2.5 text-right w-24">+Wastage</th><th className="p-2.5 text-right w-20">Indented</th>
                <th className="p-2.5 text-right w-20">Ordered</th><th className="p-2.5 text-right w-20">Received</th><th className="p-2.5 text-right w-20">Gap</th><th className="p-2.5 text-center w-24">Status</th>
              </tr></thead>
              <tbody>{demands.map((d, idx) => {
                const isGap = d.gap_qty > 0; const isFull = d.gap_qty <= 0;
                const pipelinePct = d.total_with_wastage > 0 ? Math.min(100, Math.round(d.indented_qty / d.total_with_wastage * 100)) : 0;
                return (<tr key={idx} className={`border-b border-zinc-100 ${isGap ? 'bg-red-50/30' : ''}`} data-testid={`demand-row-${idx}`}>
                  <td className="p-2.5 text-center">{isGap && <input type="checkbox" checked={selectedGaps.has(idx)} onChange={() => toggleGap(idx)} className="h-3.5 w-3.5 rounded border-zinc-300" data-testid={`demand-select-${idx}`} />}</td>
                  <td className="p-2.5"><span className="font-medium text-zinc-800">{d.material_name}</span>{d.material_code && <span className="ml-1.5 font-mono text-[9px] text-blue-600">{d.material_code}</span>}{d.spec_fingerprint && <span className="block font-mono text-[8px] text-zinc-400 mt-0.5" title={d.spec_fingerprint}>{d.spec_fingerprint.substring(0, 40)}</span>}{d.source_line_items?.length > 0 && <span className="block text-[9px] text-zinc-400 mt-0.5">From: {d.source_line_items.map(s => s.description).slice(0, 2).join(', ')}{d.source_line_items.length > 2 ? ` +${d.source_line_items.length - 2}` : ''}</span>}</td>
                  <td className="p-2.5 text-zinc-600">{d.unit}</td>
                  <td className="p-2.5 text-right text-zinc-700">{d.total_required_qty.toLocaleString('en-IN')}</td>
                  <td className="p-2.5 text-right font-medium text-zinc-800">{d.total_with_wastage.toLocaleString('en-IN')}</td>
                  <td className="p-2.5 text-right text-zinc-600">{d.indented_qty > 0 ? d.indented_qty.toLocaleString('en-IN') : '-'}</td>
                  <td className="p-2.5 text-right text-zinc-600">{d.ordered_qty > 0 ? d.ordered_qty.toLocaleString('en-IN') : '-'}</td>
                  <td className="p-2.5 text-right text-zinc-600">{d.received_qty > 0 ? d.received_qty.toLocaleString('en-IN') : '-'}</td>
                  <td className={`p-2.5 text-right font-bold ${isGap ? 'text-red-700' : 'text-emerald-700'}`}>{isGap ? d.gap_qty.toLocaleString('en-IN') : '0'}</td>
                  <td className="p-2.5 text-center">{isFull ? <span className="text-[9px] font-bold uppercase px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded">Covered</span> : pipelinePct > 0 ? <span className="text-[9px] font-bold uppercase px-2 py-0.5 bg-amber-100 text-amber-700 rounded">{pipelinePct}%</span> : <span className="text-[9px] font-bold uppercase px-2 py-0.5 bg-red-100 text-red-700 rounded">Gap</span>}</td>
                </tr>);
              })}</tbody>
            </table>
          </div>
          {skipped.length > 0 && (<div className="bg-zinc-50 border border-zinc-200 p-3"><p className="text-[10px] font-bold text-zinc-500 uppercase mb-1">Skipped ({skipped.length} items)</p><div className="flex flex-wrap gap-1.5">{skipped.map((s, i) => <span key={i} className="text-[10px] bg-white border border-zinc-200 px-2 py-0.5 text-zinc-600">{s.description}</span>)}</div></div>)}
          {data?.snapshot_date && <p className="text-[10px] text-zinc-400 text-right">Last exploded: {new Date(data.snapshot_date).toLocaleString()}{data?.created_by_name ? ` by ${data.created_by_name}` : ''}</p>}
        </>
      )}
    </div>
  );
};

// ========== MANPOWER DEMAND VIEW ==========
const ManpowerDemandView = ({ projectId, onRefresh }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deriving, setDeriving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());

  const fetchLatest = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${BACKEND_URL}/api/projects/${projectId}/manpower-demand/latest`, { withCredentials: true }); setData(res.data); } catch { }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetchLatest(); }, [fetchLatest]);

  const deriveDemand = async () => {
    setDeriving(true);
    try { const res = await axios.post(`${BACKEND_URL}/api/projects/${projectId}/manpower-demand/derive`, {}, { withCredentials: true }); setData(res.data); toast.success(`Manpower derived: ${res.data.summary?.total_roles || 0} roles, ${res.data.summary?.total_man_days || 0} man-days`); if (onRefresh) onRefresh(); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setDeriving(false);
  };

  const generatePlans = async () => {
    const indices = selectedRows.size > 0 ? Array.from(selectedRows) : undefined;
    setGenerating(true);
    try { const res = await axios.post(`${BACKEND_URL}/api/projects/${projectId}/manpower-demand/generate-plans`, { demand_indices: indices }, { withCredentials: true }); toast.success(res.data.message); setSelectedRows(new Set()); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setGenerating(false);
  };

  const toggleRow = (idx) => { const next = new Set(selectedRows); if (next.has(idx)) next.delete(idx); else next.add(idx); setSelectedRows(next); };

  const demands = data?.demands || [];
  const summary = data?.summary || {};
  const skipped = data?.skipped_items || [];
  const byType = summary.by_labour_type || {};

  if (loading) return <div className="py-8 text-center text-zinc-400 text-sm">Loading manpower data...</div>;

  return (
    <div className="space-y-6" data-testid="manpower-demand-view">
      <div className="flex items-center justify-between">
        <div><h2 className="text-lg font-bold text-zinc-900">Manpower Demand</h2><p className="text-xs text-zinc-500 mt-0.5">Derive trade/role-level manpower from BOQ recipes. Generate draft plans.</p></div>
        <div className="flex gap-2">
          <Button onClick={deriveDemand} disabled={deriving} size="sm" className="bg-blue-700 hover:bg-blue-800 text-white rounded-none text-xs" data-testid="derive-manpower-btn">{deriving ? 'Deriving...' : demands.length ? 'Refresh Demand' : 'Derive Demand'}</Button>
          {demands.length > 0 && <Button onClick={generatePlans} disabled={generating} size="sm" variant="outline" className="rounded-none border-zinc-300 text-xs" data-testid="generate-mp-plans-btn">{generating ? 'Generating...' : `Generate Draft Plans${selectedRows.size > 0 ? ` (${selectedRows.size})` : ` (${demands.length})`}`}</Button>}
        </div>
      </div>
      {demands.length === 0 && !data?.snapshot_date && (<div className="text-center py-12 bg-white border border-zinc-200"><HardHat size={40} className="mx-auto text-zinc-300 mb-3" /><p className="text-zinc-500 text-sm">No manpower data yet. Click "Derive Demand" to analyze BOQ manpower recipes.</p></div>)}
      {demands.length > 0 && (
        <>
          <div className="grid grid-cols-5 gap-3">
            <div className="bg-white border border-zinc-200 p-3"><p className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider">Total Roles</p><p className="text-xl font-bold text-zinc-900 mt-1" data-testid="mp-total-roles">{summary.total_roles}</p></div>
            <div className="bg-white border border-zinc-200 p-3"><p className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider">Total Man-Days</p><p className="text-xl font-bold text-blue-700 mt-1" data-testid="mp-total-days">{summary.total_man_days}</p></div>
            <div className="bg-white border border-zinc-200 p-3" data-testid="mp-billable-days"><p className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider">Billable Days</p><p className="text-xl font-bold text-emerald-700 mt-1">{summary.total_billable_days || 0}</p></div>
            <div className={`bg-white border p-3 ${(summary.total_wasted_days || 0) > 0 ? 'border-amber-300 bg-amber-50' : 'border-zinc-200'}`} data-testid="mp-wasted-days">
              <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider">Wasted Days</p>
              <p className={`text-xl font-bold mt-1 ${(summary.total_wasted_days || 0) > 0 ? 'text-amber-700' : 'text-zinc-400'}`}>
                {summary.total_wasted_days || 0}
                {summary.wasted_fraction > 0 && <span className="text-[10px] ml-1">({Math.round(summary.wasted_fraction * 100)}%)</span>}
              </p>
            </div>
            <div className="bg-white border border-zinc-200 p-3"><p className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider">By Type</p><div className="flex gap-2 mt-1 flex-wrap">{Object.entries(byType).map(([type, days]) => <span key={type} className={`text-[10px] font-bold px-2 py-0.5 rounded ${type === 'Skilled' ? 'bg-blue-100 text-blue-700' : type === 'Unskilled' ? 'bg-zinc-100 text-zinc-700' : 'bg-amber-100 text-amber-700'}`}>{type}: {days}d</span>)}</div></div>
          </div>
          <div className="bg-white border border-zinc-200 overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="bg-zinc-50 border-b border-zinc-200 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                <th className="p-2.5 text-left w-8"></th><th className="p-2.5 text-left">Role</th><th className="p-2.5 text-left w-24">Labour Type</th><th className="p-2.5 text-left w-28">Trade</th><th className="p-2.5 text-right w-24">BOQ Units</th><th className="p-2.5 text-right w-24">Man-Days</th><th className="p-2.5 text-right w-24" title="Days you'll actually pay for (rounded up to whole days)">Billable</th><th className="p-2.5 text-right w-24" title="Days lost — qty smaller than 1 worker's daily output">Wasted</th>
              </tr></thead>
              <tbody>{demands.map((d, idx) => {
                const wasted = d.total_wasted_days || 0;
                const billable = d.total_billable_days || 0;
                const minQty = d.min_qty_full_day || 0;
                const wfrac = billable > 0 ? wasted / billable : 0;
                return (
                <tr key={idx} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`mp-demand-row-${idx}`}>
                  <td className="p-2.5 text-center"><input type="checkbox" checked={selectedRows.has(idx)} onChange={() => toggleRow(idx)} className="h-3.5 w-3.5 rounded border-zinc-300" /></td>
                  <td className="p-2.5"><span className="font-medium text-zinc-800">{d.role_name || 'General'}</span>{minQty > 0 && <span className="block text-[9px] text-zinc-400 mt-0.5">1 worker = {minQty} units/day</span>}{d.source_line_items?.length > 0 && <span className="block text-[9px] text-zinc-400 mt-0.5">From: {d.source_line_items.map(s => s.description).slice(0, 2).join(', ')}{d.source_line_items.length > 2 ? ` +${d.source_line_items.length - 2}` : ''}</span>}</td>
                  <td className="p-2.5"><span className={`text-[10px] font-bold px-2 py-0.5 rounded ${d.labour_type === 'Skilled' ? 'bg-blue-100 text-blue-700' : d.labour_type === 'Unskilled' ? 'bg-zinc-100 text-zinc-700' : 'bg-amber-100 text-amber-700'}`}>{d.labour_type}</span></td>
                  <td className="p-2.5 text-zinc-600">{d.trade_name || '-'}</td>
                  <td className="p-2.5 text-right text-zinc-700">{d.total_required_units.toLocaleString('en-IN')}</td>
                  <td className="p-2.5 text-right font-bold text-zinc-900">{d.total_man_days.toLocaleString('en-IN')}</td>
                  <td className="p-2.5 text-right text-emerald-700 font-medium">{billable.toLocaleString('en-IN')}</td>
                  <td className={`p-2.5 text-right font-medium ${wfrac >= 0.20 ? 'text-amber-700' : 'text-zinc-400'}`}>{wasted > 0 ? wasted.toLocaleString('en-IN') : '—'}</td>
                </tr>);
              })}</tbody>
              <tfoot><tr className="bg-zinc-50 border-t border-zinc-200"><td colSpan={5} className="p-2.5 text-right text-[10px] font-bold text-zinc-500 uppercase">Total</td><td className="p-2.5 text-right font-bold text-zinc-900">{summary.total_man_days}</td><td className="p-2.5 text-right font-bold text-emerald-700">{summary.total_billable_days || 0}</td><td className={`p-2.5 text-right font-bold ${(summary.total_wasted_days || 0) > 0 ? 'text-amber-700' : 'text-zinc-400'}`}>{summary.total_wasted_days || '—'}</td></tr></tfoot>
            </table>
          </div>
          {skipped.length > 0 && (<div className="bg-zinc-50 border border-zinc-200 p-3"><p className="text-[10px] font-bold text-zinc-500 uppercase mb-1">Skipped ({skipped.length} items)</p><div className="flex flex-wrap gap-1.5">{skipped.map((s, i) => <span key={i} className="text-[10px] bg-white border border-zinc-200 px-2 py-0.5 text-zinc-600">{s.description}</span>)}</div></div>)}
          {data?.snapshot_date && <p className="text-[10px] text-zinc-400 text-right">Last derived: {new Date(data.snapshot_date).toLocaleString()}{data?.created_by_name ? ` by ${data.created_by_name}` : ''}</p>}
        </>
      )}
    </div>
  );
};

// ========== RECIPE COVERAGE CARD (compact) ==========
export const RecipeCoverageCard = ({ projectId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${BACKEND_URL}/api/projects/${projectId}/recipe-coverage`, { withCredentials: true }); setData(res.data); } catch { }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="mt-4 text-xs text-zinc-400">Loading recipe coverage...</div>;
  if (!data || data.total_items === 0) return null;

  const pct = data.coverage_percentage;
  const barColor = pct >= 80 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500';
  const flaggedItems = data.items?.filter(i => i.flags?.length > 0) || [];

  return (
    <div className="mt-4 bg-white border border-zinc-200 p-4" data-testid="recipe-coverage-card">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-blue-700" />
          <span className="text-xs font-bold uppercase tracking-wider text-zinc-700">Recipe Coverage</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-zinc-500">{data.fully_covered}/{data.total_items} items covered</span>
          <span className={`text-sm font-bold ${pct >= 80 ? 'text-emerald-700' : pct >= 50 ? 'text-amber-700' : 'text-red-700'}`} data-testid="recipe-coverage-pct">{pct}%</span>
        </div>
      </div>
      <div className="w-full h-2 bg-zinc-100 rounded-full overflow-hidden"><div className={`h-full ${barColor} transition-all duration-500`} style={{ width: `${pct}%` }} /></div>
      <div className="flex gap-3 mt-2 text-[10px]">
        <span className="text-zinc-500">Material Recipe: <strong className="text-zinc-700">{data.with_material_recipe}</strong></span>
        <span className="text-zinc-500">Manpower Recipe: <strong className="text-zinc-700">{data.with_manpower_recipe}</strong></span>
        <span className="text-zinc-500">Master Linked: <strong className="text-zinc-700">{data.with_master_linkage}</strong></span>
      </div>
      {flaggedItems.length > 0 && (
        <div className="mt-2">
          <button onClick={() => setExpanded(!expanded)} className="text-[10px] text-blue-600 hover:text-blue-800 font-semibold" data-testid="toggle-flagged-items">
            {expanded ? 'Hide' : 'Show'} {flaggedItems.length} flagged item{flaggedItems.length !== 1 ? 's' : ''}
          </button>
          {expanded && (
            <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
              {flaggedItems.map((item, idx) => (
                <div key={idx} className="flex items-start justify-between text-[10px] bg-zinc-50 border border-zinc-100 px-2 py-1.5 rounded">
                  <div><span className="text-zinc-800 font-medium">{item.description}</span><span className="text-zinc-400 ml-1.5">{item.quantity} {item.unit}</span></div>
                  <div className="flex gap-1 shrink-0 ml-2">{item.flags.map((f, fi) => <span key={fi} className={`px-1.5 py-0.5 rounded text-[8px] font-bold uppercase ${f === 'missing_material_recipe' ? 'bg-amber-100 text-amber-700' : f === 'missing_manpower_recipe' ? 'bg-orange-100 text-orange-700' : 'bg-zinc-200 text-zinc-600'}`}>{f.replace(/_/g, ' ').replace('missing ', '')}</span>)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
